<script setup>
import {ref, onMounted, inject} from 'vue'
import {useRouter} from 'vue-router'
import axios from 'axios'
import {getPreviousConferenceYear} from '@/composables/usePreviousConference'

const router = useRouter()
const backEndURL = process.env.VUE_APP_BACKEND_URL

const contentData = inject('contentData')
const contentFileName = process.env.VUE_APP_CONTENT_FILE_NAME

const navItems = ref([])
const previousConferences = ref([])
const isNavOpen = ref(false)
const openMenuIndex = ref(null)

onMounted(async () => {
    await getNavigationItems()
    await getPreviousConferences()
})

async function getNavigationItems() {
    try {
        const res = await axios.get(`${backEndURL}/api/navigation-items`)
        navItems.value = res.data
    } catch (err) {
        console.error(err)
        alert('Error loading navigation items')
    }
}

/**
 * Input example: GET /api/previous-conferences
 * Output example: previousConferences.value = [{ id: 3, year: 2026, date: "Personalized Health 2026" }]
 */
async function getPreviousConferences() {
    try {
        const res = await axios.get(`${backEndURL}/api/previous-conferences`)
        previousConferences.value = Array.isArray(res.data) ? res.data : []
    } catch (err) {
        console.error(err)
    }
}

function closeMenus() {
    isNavOpen.value = false
    openMenuIndex.value = null
}

function isHashLink(href) {
    return typeof href === 'string' && href.charAt(0) === '#'
}

/**
 * Input example: href = '#overview' on '/abstract/GuidedCityTour'
 * Output example: navigates to '/#overview'
 */
function handleNavClick(event, href) {
    closeMenus()

    if (!isHashLink(href)) {
        return
    }

    event.preventDefault()
    router.push({path: '/', hash: href})
}

const handleBurgerClick = () => {
    isNavOpen.value = !isNavOpen.value
}

function toggleMenu(idx) {
    openMenuIndex.value = openMenuIndex.value === idx ? null : idx
}

/**
 * Input example: user clicks the Home navigation item from a previous-conference page
 * Output example: navigates to /
 */
function navigateHome() {
    closeMenus()
    router.push({path: '/'})
}

/**
 * Input example: conference = { id: 3, year: null }
 * Output example: "Invalid previous conference year"
 */
function getPreviousConferenceYearLabel(conference) {
    return getPreviousConferenceYear(conference) ?? 'Invalid previous conference year'
}

/**
 * Input example: conference = { id: 3, year: null }
 * Output example: navigates to /previous-conferences/invalid
 */
function navigateToPreviousConference(conference) {
    closeMenus()
    const conferenceYear = getPreviousConferenceYear(conference)
    const routeYear = conferenceYear ?? 'invalid'

    router.push({path: `/previous-conferences/${routeYear}`})
}
</script>

<template>
    <!-- TOP NAVBAR -->
    <nav class="py-2 bg-white top-0 z-50">
        <div class="container mx-auto">
            <div class="relative flex items-center justify-between">
                <!-- LOGO -->
                <div class="w-auto">
                    <a class="inline-block" href="https://ethz.ch/" target="_blank" rel="noopener noreferrer">
                        <img :src="contentData.left_logo" alt="ETH Zürich logo" class="h-10 w-auto" />
                    </a>
                </div>

                <!-- DESKTOP NAV LINKS  -->
                <div
                    class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 hidden xl:block">
                    <ul class="flex items-center">
                        <li class="relative font-heading mr-12 text-base w-auto">
                            <a class="hover:text-blue-600 cursor-pointer" href="/" @click.prevent="navigateHome">
                                Home
                            </a>
                        </li>

                        <li
                            v-for="(item, idx) in navItems.filter(i => i.display)"
                            :key="item.id"
                            class="relative font-heading mr-12 text-base w-auto">
                            <a
                                :target="isHashLink(item.href) ? '_self' : '_blank'"
                                v-if="!item.submenu_items?.length"
                                class="hover:text-blue-600 cursor-pointer"
                                :href="item.href"
                                @click="handleNavClick($event, item.href)">
                                {{ item.title }}
                            </a>

                            <template v-else>
                                <a class="hover:text-blue-600 cursor-pointer" @click="toggleMenu(idx)">
                                    {{ item.title }}
                                </a>

                                <ul
                                    class="absolute top-full left-0 mt-2 bg-white border shadow-md rounded-md z-50"
                                    :class="openMenuIndex === idx ? 'block' : 'hidden'">
                                    <li
                                        v-for="[subTitle, subHref] in item.submenu_items"
                                        :key="subHref"
                                        class="whitespace-nowrap px-4 py-2 hover:bg-blue-100 z-50">
                                        <a
                                            :target="isHashLink(subHref) ? '_self' : '_blank'"
                                            :href="subHref"
                                            @click="handleNavClick($event, subHref)">
                                            {{ subTitle }}
                                        </a>
                                    </li>
                                </ul>
                            </template>
                        </li>

                        <li
                            v-if="previousConferences.length > 0"
                            class="relative font-heading mr-12 text-base w-auto">
                            <a class="hover:text-blue-600 cursor-pointer" @click="toggleMenu('previous-desktop')">
                                Previous
                            </a>

                            <ul
                                class="absolute top-full left-0 mt-2 min-w-[220px] rounded-md border bg-white shadow-md z-50"
                                :class="openMenuIndex === 'previous-desktop' ? 'block' : 'hidden'">
                                <li
                                    v-for="conference in previousConferences"
                                    :key="conference.id"
                                    class="whitespace-nowrap px-4 py-2 hover:bg-blue-100 z-50">
                                    <button
                                        class="block w-full text-left"
                                        @click="navigateToPreviousConference(conference)">
                                        {{ getPreviousConferenceYearLabel(conference) }}
                                    </button>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>

                <!-- RIGHT-SIDE NEXUS LOGO -->
                <div class="w-auto hidden xl:block">
                    <a class="inline-block" href="https://www.nexus.ethz.ch/" target="_blank" rel="noopener noreferrer">
                        <img src="/assets/pht/logo_new.png" alt="NEXUS logo" class="h-10 w-auto" />
                    </a>
                </div>

                <!-- BURGER ICON (MOBILE) -->
                <div class="w-auto xl:hidden">
                    <button
                        class="inline-flex w-14 h-14 justify-center items-center bg-gray-500 hover:bg-gray-600 rounded-full"
                        @click="handleBurgerClick">
                        <svg
                            width="20"
                            height="10"
                            viewBox="0 0 20 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M19 9H1M19 1H1"
                                stroke="white"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    </nav>

    <!-- MOBILE NAVIGATION -->

    <div v-if="isNavOpen" class="fixed inset-0 z-[110] xl:hidden">
        <div class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" @click="closeMenus"></div>

        <nav
            class="mobile-navigation relative flex h-full w-full flex-col bg-white shadow-2xl"
            aria-label="Mobile navigation">
            <div class="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                <a class="inline-flex" href="https://ethz.ch/" target="_blank" rel="noopener noreferrer">
                    <img :src="contentData.left_logo" alt="ETH Zürich logo" class="h-8 w-auto" />
                </a>
                <button
                    type="button"
                    class="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-slate-700 transition hover:border-blue-600 hover:text-blue-600"
                    aria-label="Close navigation"
                    @click="closeMenus">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M2 2L16 16M16 2L2 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                    </svg>
                </button>
            </div>

            <div class="flex-1 overflow-y-auto px-5 py-5">
                <ul class="space-y-1">
                    <li>
                        <button
                            type="button"
                            class="font-heading flex min-h-12 w-full items-center rounded-md px-4 py-3 text-left text-base font-medium text-slate-900 transition hover:bg-green-50 hover:text-blue-700"
                            @click="navigateHome">
                            Home
                        </button>
                    </li>

                    <li v-for="item in navItems.filter(i => i.display)" :key="item.id" class="border-b border-slate-200 last:border-b-0">
                        <a
                            v-if="!item.submenu_items?.length"
                            class="font-heading flex min-h-12 items-center rounded-md px-4 py-3 text-base font-medium text-slate-900 transition hover:bg-green-50 hover:text-blue-700"
                            :href="item.href"
                            @click="handleNavClick($event, item.href)">
                            {{ item.title }}
                        </a>

                        <div v-else class="py-4">
                            <div class="font-heading px-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                                {{ item.title }}
                            </div>
                            <ul class="mt-3 space-y-1 border-l-2 border-green-200 pl-3">
                                <li v-for="[subTitle, subHref] in item.submenu_items" :key="subHref">
                                    <a
                                        class="block rounded-md px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-green-50 hover:text-blue-700"
                                        :href="subHref"
                                        @click="handleNavClick($event, subHref)">
                                        {{ subTitle }}
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </li>
                </ul>

                <section v-if="previousConferences.length > 0" class="mt-7 rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <h2 class="font-heading text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Previous</h2>
                    <div class="mt-3 grid grid-cols-2 gap-2">
                        <button
                            v-for="conference in previousConferences"
                            :key="conference.id"
                            class="rounded-md border border-slate-300 bg-white px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:border-blue-600 hover:text-blue-700"
                            @click="navigateToPreviousConference(conference)">
                            {{ getPreviousConferenceYearLabel(conference) }}
                        </button>
                    </div>
                </section>
            </div>
        </nav>
    </div>
</template>

<style scoped>
nav {
    position: relative;
    z-index: 100;
    overflow: visible;
}

.mobile-navigation {
    overflow: hidden;
}
</style>
